import { useEffect, useMemo, useState } from "react";
import { ORDEM_EQUIPAMENTO, ROTULO_SECAO } from "../config";
import { ordenarPorCampos } from "../ordem";
import type { Row } from "../types";
import { api, ApiError, type DadosLaudo, type DespacharDados, type RelatorioDados } from "../api";
import { IconClose, IconCheck, IconDownload, IconFlask, IconSend } from "../icons";
import StatusCardSelector from "./StatusCardSelector";
import { usePermissoes } from "../permissoes";
import { CONDICOES_OLEO_NORMA } from "../normas-vocabulario";
import { calcularTotaisGases } from "../totais-gases";

interface Props {
  analise: Row;
  onCancel: () => void;
  onSaved: () => void; // recarrega a lista após salvar/gerar/despachar
  /** Abre direto numa aba (ex.: "diagnostico", vindo do painel da OS). */
  abaInicial?: string;
  /**
   * Motivo pelo qual o DIAGNÓSTICO ainda não pode ser feito (null = liberado).
   *
   * A regra é do laboratório: o diagnóstico só abre quando TODAS as amostras da
   * OS estiverem concluídas — com metade da OS ainda na bancada, o veredito sai
   * por cima de dado que vai mudar. O servidor recusa igual (ver
   * server/analise-diagnostico.ts); aqui a aba explica o que falta em vez de
   * deixar a pessoa rodar e levar um erro.
   */
  diagnosticoBloqueado?: string | null;
}

const STATUS_SECAO_OPCOES = ["Aprovado", "Com ressalvas", "Reprovado"];
const STATUS_SECAO_HINTS = [
  "Resultado dentro dos valores de referência",
  "Resultado requer atenção ou acompanhamento",
  "Resultado fora dos valores de referência",
];

type SecaoKey =
  | "identificacao"
  | "fisicoQuimica"
  | "agdGases"
  | "pcb"
  | "furfuraldeido"
  | "diagnostico"
  | "despacho";

/** Cor da etiqueta conforme o veredito (usa as pills que já existem). */
function vereditoTone(v: string | null | undefined): string {
  if (v === "Normal") return "green";
  if (v === "Atenção") return "amber";
  if (v === "Crítico") return "red";
  return "gray";
}

/*
 * Os nomes das abas são as SIGLAS que o laboratório usa no dia a dia (FQ, CR,
 * PCB, 2-FALL), com o nome por extenso ao lado. As mesmas siglas aparecem na
 * listagem e no filtro da aba Análises — é a mesma linguagem em todo lugar.
 * As chaves internas continuam as de sempre (fisicoQuimica/agdGases/...), que
 * é o que o Relatórios manda e o que está gravado no banco.
 */
const SECOES: { key: SecaoKey; label: string }[] = [
  { key: "identificacao", label: "Identificação" },
  { key: "fisicoQuimica", label: ROTULO_SECAO.fisicoQuimica! },
  { key: "agdGases", label: ROTULO_SECAO.agdGases! },
  { key: "pcb", label: ROTULO_SECAO.pcb! },
  { key: "furfuraldeido", label: ROTULO_SECAO.furfuraldeido! },
  { key: "diagnostico", label: "Diagnóstico" },
  { key: "despacho", label: "Despacho" },
];

// Seções de análise que o Relatórios pode solicitar (FQ/CR/PCB/2 FALL). As abas
// "Identificação" e "Despacho" são SEMPRE exibidas; as de análise só aparecem se
// foram solicitadas. Retrocompat: sem seleção → mostra todas.
const SECOES_ANALISE: SecaoKey[] = ["fisicoQuimica", "agdGases", "pcb", "furfuraldeido"];

/** Lê a lista de seções solicitadas (JSONB) da análise; null/vazio = todas. */
function lerAnalisesSelecionadas(v: unknown): SecaoKey[] | null {
  if (!Array.isArray(v)) return null;
  const validas = v.filter((k): k is SecaoKey => SECOES_ANALISE.includes(k as SecaoKey));
  return validas.length ? validas : null;
}

/**
 * AS QUATRO CONDIÇÕES do óleo, iguais às cadastradas nos limites (a comparação
 * é por texto exato — mudar aqui sem mudar a carga da NBR 10576 faz o sistema
 * parar de achar a faixa).
 */
// A lista mora em src/normas-vocabulario.ts (uma fonte para tela e servidor);
// aqui fica só o reexport, porque meia dúzia de telas já importam deste arquivo.
export const CONDICOES_OLEO = CONDICOES_OLEO_NORMA;

type Campos = Record<string, string>;

/** Lê um objeto vindo do banco (JSONB, pode vir null) como Campos. */
function lerObjeto(v: unknown): Campos {
  if (v && typeof v === "object") return v as Campos;
  return {};
}

/**
 * Tela de configuração da análise de óleo (o "laudo completo"): preenche as 4
 * seções (mesmos campos do template analise_de_oleo.docx), roda o diagnóstico
 * pelos limites normativos, pede a aprovação do segundo olhar e despacha ao
 * Relatórios.
 *
 * O LANÇAMENTO do dia a dia NÃO é aqui: quem está na bancada usa a tela enxuta
 * (components/LancamentoResultados.tsx), que mostra só os resultados do ensaio
 * em que a pessoa está. Esta aqui é a etapa de fechar o laudo.
 */
export default function AnaliseOleoConfig({
  analise,
  onCancel,
  onSaved,
  abaInicial,
  diagnosticoBloqueado = null,
}: Props) {
  const [aba, setAba] = useState<SecaoKey>((abaInicial as SecaoKey) || "identificacao");

  const [identificacao, setIdentificacao] = useState<Campos>(() => lerObjeto(analise.identificacao));
  /**
   * A CONDIÇÃO DO ÓLEO — o dado que escolhe a tabela da NBR 10576.
   *
   * É coluna da amostra (e não campo do laudo): o mesmo transformador é "em
   * serviço" numa coleta e "após regeneração" na seguinte. Sem ela o sistema
   * não seleciona a faixa e deixa o diagnóstico em branco, de propósito — é o
   * item 9.7 da base de limites.
   */
  const [condicaoOleo, setCondicaoOleo] = useState<string>(String(analise.condicao_oleo ?? ""));

  /* ------------------ VÍNCULO COM O EQUIPAMENTO ------------------ *
   * É do equipamento que saem tipo de óleo, classe de tensão e tipo de
   * equipamento — três dos cinco eixos que selecionam a faixa da norma. Amostra
   * sem vínculo não recebe diagnóstico automático nenhum, e até aqui a tela
   * mandava "vincule o equipamento" sem oferecer como fazer.
   */
  const [equipamentoId, setEquipamentoId] = useState<number | null>(
    analise.equipamento_id == null ? null : Number(analise.equipamento_id),
  );
  const [equipamentos, setEquipamentos] = useState<Row[] | null>(null);
  const [vinculando, setVinculando] = useState(false);

  useEffect(() => {
    // A lista inteira, filtrada no cliente: o cadastro do laboratório é
    // pequeno, e uma rota de busca por cliente seria mais peça para manter.
    api
      .list("equipamentos")
      .then((linhas) => setEquipamentos(linhas))
      .catch(() => setEquipamentos([]));
  }, []);

  /**
   * Os equipamentos do MESMO cliente primeiro — é quase sempre um deles —, e
   * cada grupo em ordem alfabética (proprietário → TAG → nº de série; ver
   * ORDEM_EQUIPAMENTO em config.ts e src/ordem.ts).
   */
  const equipamentosOrdenados = useMemo(() => {
    const todos = ordenarPorCampos(equipamentos ?? [], ORDEM_EQUIPAMENTO);
    const cliente = String(analise.cliente ?? "").trim().toLowerCase();
    const mesmo = (e: Row) => String(e.cliente ?? "").trim().toLowerCase() === cliente;
    return [...todos.filter(mesmo), ...todos.filter((e) => !mesmo(e))];
  }, [equipamentos, analise.cliente]);

  const equipamentoAtual = useMemo(
    () => (equipamentos ?? []).find((e) => Number(e.id) === equipamentoId) ?? null,
    [equipamentos, equipamentoId],
  );

  async function vincular(novoId: number | null) {
    setVinculando(true);
    try {
      await api.analises.vincularEquipamento(analise.id, novoId);
      setEquipamentoId(novoId);
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Não foi possível vincular o equipamento");
    } finally {
      setVinculando(false);
    }
  }
  const [fisicoQuimica, setFisicoQuimica] = useState<Campos>(() => lerObjeto(analise.fisico_quimica));
  const [agdGases, setAgdGases] = useState<Campos>(() => lerObjeto(analise.agd_gases));
  const [pcb, setPcb] = useState<Campos>(() => lerObjeto(analise.pcb));
  const [furfuraldeido, setFurfuraldeido] = useState<Campos>(() => lerObjeto(analise.furfuraldeido));

  const [parecer, setParecer] = useState<string>(analise.parecer ?? "");
  const [responsavel, setResponsavel] = useState<string>(analise.responsavel ?? "");

  /* --- Diagnóstico (motor + aprovação) --- */
  const [diag, setDiag] = useState<any>(null);
  const [diagInfo, setDiagInfo] = useState<{ mensagem?: string; aviso?: string | null } | null>(null);
  /** Tipo de falha pelas razões entre gases (NBR 7274) — pergunta separada da severidade. */
  const [razoes, setRazoes] = useState<any>(null);
  const [diagnosticando, setDiagnosticando] = useState(false);
  const [aprovacao, setAprovacao] = useState<any>(null);

  const [salvando, setSalvando] = useState(false);
  const [despachando, setDespachando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /*
   * O PARECER DE CADA SEÇÃO NÃO É LANÇAMENTO — é diagnóstico.
   *
   * Cada aba de ensaio tem, no fim, dois campos que NÃO são medida: o "Status
   * da seção" (o veredito) e o "Diagnóstico / conclusão" (o texto que vai para
   * o laudo). Eles ficavam abertos junto com os valores, e por eles qualquer
   * pessoa escrevia parecer no meio do lançamento — sem a permissão de
   * diagnóstico e antes de a OS estar concluída, que são exatamente as duas
   * condições da etapa de diagnóstico.
   *
   * Travados, continuam VISÍVEIS: quem lança os números precisa ler o parecer
   * que já existe. O servidor aplica a mesma regra (ver salvarConfigAnalise em
   * server/analises.ts) — aqui é conforto, lá é a regra.
   */
  const { diagnostico: podeDiagnosticar } = usePermissoes();
  const jaDespachada = String(analise.status ?? "") === "Despachada";
  const parecerTravado = !podeDiagnosticar || !!diagnosticoBloqueado || jaDespachada;
  const motivoParecerTravado = jaDespachada
    ? "Amostra já despachada — o laudo dela não muda mais."
    : !podeDiagnosticar
      ? "O parecer é da etapa de diagnóstico, restrita a quem tem a permissão."
      : diagnosticoBloqueado
        ? diagnosticoBloqueado
        : "";

  /**
   * Roda o motor de diagnóstico. Salva ANTES: o veredito é sobre os valores que
   * estão no banco, e diagnosticar o que ainda não foi salvo mostraria um
   * resultado que não corresponde ao laudo.
   */
  async function rodarDiagnostico(comIA: boolean) {
    setErro(null);
    setOk(null);
    setDiagnosticando(true);
    try {
      await salvar();
      const r = await api.analises.diagnosticar(analise.id, comIA);
      setDiag(r.diagnostico);
      setRazoes(r.razoes ?? null);
      setDiagInfo({ mensagem: r.mensagem, aviso: r.aviso });
      await carregarAprovacao();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao gerar o diagnóstico.");
    } finally {
      setDiagnosticando(false);
    }
  }

  async function carregarAprovacao() {
    try {
      setAprovacao(await api.analises.situacaoAprovacao(analise.id));
    } catch {
      /* a tela não pode quebrar por causa do painel de aprovação */
    }
  }

  async function enviarParaAprovacao() {
    setErro(null);
    setDiagnosticando(true);
    try {
      const r = await api.analises.solicitarAprovacao(analise.id);
      setOk(r.mensagem ?? "Enviado para aprovação.");
      await carregarAprovacao();
      onSaved();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao enviar para aprovação.");
    } finally {
      setDiagnosticando(false);
    }
  }
  const [ok, setOk] = useState<string | null>(null);
  const laudoNome = analise.laudo_nome ?? null;

  // O Laboratório tem um template PADRÃO embutido (analise_de_oleo.docx): sempre
  // é possível gerar o laudo preenchido. `templateDaSolicitacao` só indica se a
  // solicitação trouxe um template próprio (caso contrário usa-se o padrão).
  const templateDaSolicitacao = Boolean(analise.tem_template);
  /** A amostra tem ensaio que ainda sai no modelo antigo (Furanos)? Sem seleção = os quatro. */
  const pedeModeloAntigo = (() => {
    const sel = lerAnalisesSelecionadas(analise.analises_selecionadas);
    return !sel || sel.includes("furfuraldeido");
  })();
  /*
   * OS RELATÓRIOS (modelos de Gases e PCB). A prévia diz, sem gerar nada, o
   * que cada campo em branco vai mostrar (o valor automático entra como texto
   * cinza dentro do campo) e o Nº de cada relatório.
   */
  const [relDados, setRelDados] = useState<RelatorioDados | null>(null);
  const [baixando, setBaixando] = useState<string | null>(null);
  const carregarRelDados = () =>
    api.analises
      .relatorioDados(analise.id)
      .then(setRelDados)
      .catch(() => setRelDados(null)); // sem a prévia os campos só ficam sem a dica
  useEffect(() => {
    void carregarRelDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analise.id]);

  /** "WEG — cadastro do equipamento": o que o relatório mostra se o campo ficar em branco. */
  function sugestao(chave: string): string | undefined {
    const c = relDados?.campos[chave];
    if (!c?.automatico) return undefined;
    return c.origem ? `${c.automatico} — ${c.origem}` : c.automatico;
  }

  /**
   * Baixa o relatório da amostra inteira (`ensaio` nulo — o mesmo arquivo que
   * vai ao Relatórios) ou o de um ensaio só. Salva antes: o relatório tem de
   * sair com o que está na tela, não com o que estava no banco.
   */
  async function baixarRelatorio(ensaio: string | null) {
    setErro(null);
    setBaixando(ensaio ?? "amostra");
    try {
      if (!jaDespachada && !(await salvar())) return;
      if (ensaio) await api.analises.baixarRelatorioEnsaio(analise.id, ensaio);
      else if (jaDespachada) await api.analises.baixarLaudo(analise.id, laudoNome ?? `analise-oleo-OS-${analise.os_numero}.docx`);
      else await api.analises.gerarRelatorio(analise.id, String(analise.os_numero ?? ""));
      setOk("Relatório baixado.");
      await carregarRelDados(); // o Nº pode ter acabado de ser dado
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível gerar o relatório.");
    } finally {
      setBaixando(null);
    }
  }

  // Uma operação por vez: Salvar/Gerar/Despachar compartilham estado no servidor
  // (laudo_base64) — cliques concorrentes gerariam laudos em ordem indeterminada.
  const ocupado = salvando || despachando || baixando !== null;

  const dadosLaudo: DadosLaudo = useMemo(
    () => ({ identificacao, fisicoQuimica, agdGases, pcb, furfuraldeido, condicao_oleo: condicaoOleo }),
    [identificacao, fisicoQuimica, agdGases, pcb, furfuraldeido, condicaoOleo],
  );

  // Abas exibidas: Identificação e Despacho sempre; as seções de análise só se
  // foram solicitadas pelo Relatórios (FQ/CR/PCB/2 FALL). Sem seleção → todas.
  const secoesVisiveis = useMemo(() => {
    const selecionadas = lerAnalisesSelecionadas(analise.analises_selecionadas);
    if (!selecionadas) return SECOES;
    return SECOES.filter(
      (s) =>
        s.key === "identificacao" ||
        s.key === "diagnostico" ||
        s.key === "despacho" ||
        selecionadas.includes(s.key),
    );
  }, [analise.analises_selecionadas]);

  function set(secao: SecaoKey, campo: string, valor: string) {
    const setters: Partial<Record<SecaoKey, (fn: (c: Campos) => Campos) => void>> = {
      identificacao: (fn) => setIdentificacao(fn),
      fisicoQuimica: (fn) => setFisicoQuimica(fn),
      agdGases: (fn) => setAgdGases(fn),
      pcb: (fn) => setPcb(fn),
      furfuraldeido: (fn) => setFurfuraldeido(fn),
    };
    setters[secao]?.((c) => ({ ...c, [campo]: valor }));
  }

  async function salvar(): Promise<boolean> {
    setSalvando(true);
    setErro(null);
    setOk(null);
    try {
      await api.analises.salvarConfig(analise.id, dadosLaudo);
      setOk("Dados salvos.");
      return true;
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao salvar os dados");
      return false;
    } finally {
      setSalvando(false);
    }
  }

  async function despachar() {
    setDespachando(true);
    setErro(null);
    setOk(null);
    try {
      // Salva o preenchimento mais recente ANTES do despacho. O servidor
      // regenera o laudo a partir do template (só com as seções selecionadas)
      // com estes dados e envia ao Relatórios.
      await api.analises.salvarConfig(analise.id, dadosLaudo);
      const dados: DespacharDados = {
        // O resultado não é mais escolhido manualmente na tela — mantém o valor
        // já gravado, ou "Aprovado" por padrão (contrato do Relatórios exige um).
        resultado: (analise.resultado || "").trim() || "Aprovado",
        parecer: parecer.trim(),
        responsavel: responsavel.trim(),
      };
      await api.analises.despachar(analise.id, dados);
      // Confirmação clara ANTES de fechar (o modal fechava em silêncio — parecia
      // que "não tinha sido enviada"). Fecha e recarrega a lista logo em seguida.
      setOk("Análise despachada ao Relatórios! A OS foi liberada para a mesclagem de relatórios.");
      setTimeout(() => onSaved(), 1500);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao despachar a análise");
    } finally {
      setDespachando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card modal-wide modal-analise" onMouseDown={(e) => e.stopPropagation()}>
        {/* Mesmo cabeçalho do formulário de cadastro (ícone + de quem é o
            registro): o popup dizia só "OS 2661" e a pessoa tinha de fechar
            para lembrar de qual cliente/equipamento era a amostra. */}
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconFlask size={20} />
            </span>
            <div>
              <h3>Análise de óleo — OS {analise.os_numero}</h3>
              <p className="rf-head-sub">
                {String(analise.cliente ?? "cliente não informado")}
                {analise.equipamento ? ` · ${analise.equipamento}` : ""}
                {analise.status ? ` · ${analise.status}` : ""}
              </p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancel} title="Fechar (Esc)">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-tabs">
          {secoesVisiveis.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`modal-tab${aba === s.key ? " active" : ""}`}
              onClick={() => setAba(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {/* FQ, Gases e PCB têm modelo próprio; o aviso do modelo padrão só vale para Furanos. */}
          {!templateDaSolicitacao && pedeModeloAntigo && (
            <div className="modal-notice">
              <span className="modal-notice-ic">ℹ️</span>
              <span>
                Furanos sai no <strong>modelo padrão de Análise de Óleo</strong> do Laboratório (esta
                solicitação não trouxe um template próprio); Físico-Químico, Gases Dissolvidos e PCB saem nos
                modelos de relatório do Laboratório.
              </span>
            </div>
          )}

          {aba === "identificacao" && (
            <>
              <p className="modal-tab-hint">
                Dados da amostra/equipamento — preenchidos uma vez, usados nas 4 seções do laudo.
              </p>
              {/*
                A CONDIÇÃO vem primeiro e sozinha: é ela que decide QUAL tabela
                da norma vale para esta amostra (em uso, novo, termovácuo ou
                regeneração). Sem ela, o diagnóstico automático não sai.
              */}
              <label className="field field-wide cfg-condicao">
                <span>
                  Condição do óleo <strong>·</strong> escolhe a tabela de limites da NBR 10576
                </span>
                <select value={condicaoOleo} onChange={(e) => setCondicaoOleo(e.target.value)}>
                  <option value="">— não informada (o diagnóstico sai em branco) —</option>
                  {CONDICOES_OLEO.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {!condicaoOleo && (
                  <small className="cfg-condicao-aviso">
                    Enquanto isto ficar em branco, o sistema não escolhe os limites e o diagnóstico
                    do Físico-Químico fica para preenchimento manual.
                  </small>
                )}
              </label>

              {/*
                O EQUIPAMENTO — de onde saem tipo de óleo, classe de tensão,
                tipo de equipamento e arranjo do comutador. São 4 dos 5 eixos
                que escolhem a faixa da norma: sem vínculo, o diagnóstico
                automático não tem limite nenhum para comparar.

                O sistema tenta vincular sozinho ao salvar esta aba (fabricante
                + número de série). Este seletor é para quando não casar — ou
                para corrigir um casamento errado.
              */}
              <label className="field field-wide cfg-condicao">
                <span>
                  Equipamento cadastrado <strong>·</strong> traz os limites da norma para esta
                  amostra
                </span>
                <select
                  value={equipamentoId == null ? "" : String(equipamentoId)}
                  disabled={vinculando || equipamentos === null}
                  onChange={(e) => vincular(e.target.value === "" ? null : Number(e.target.value))}
                >
                  <option value="">
                    {equipamentos === null ? "carregando…" : "— sem vínculo —"}
                  </option>
                  {equipamentosOrdenados.map((eq) => (
                    <option key={String(eq.id)} value={String(eq.id)}>
                      {[
                        eq.tag,
                        eq.tipo_equipamento,
                        eq.numero_serie ? `nº ${eq.numero_serie}` : null,
                        eq.cliente,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </option>
                  ))}
                </select>
                {equipamentoAtual ? (
                  <small className="cfg-condicao-aviso cfg-vinculo-ok">
                    {[
                      equipamentoAtual.tipo_oleo ? `óleo ${equipamentoAtual.tipo_oleo}` : null,
                      equipamentoAtual.classe_tensao,
                      equipamentoAtual.tipo_equipamento,
                      equipamentoAtual.arranjo_cdc,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "cadastro sem tipo de óleo/classe de tensão — complete no cadastro de Equipamentos"}
                  </small>
                ) : (
                  <small className="cfg-condicao-aviso">
                    Sem equipamento vinculado o sistema não sabe a classe de tensão nem o tipo de
                    óleo, e o diagnóstico automático fica em branco. Salvar esta aba com fabricante e
                    número de série faz o sistema procurar sozinho.
                  </small>
                )}
              </label>

              <div className="form-grid">
                <Campo label="Nº da coleta / laudo" value={identificacao.numeroAmostra} onChange={(v) => set("identificacao", "numeroAmostra", v)} />
                <Campo label="Local de instalação" value={identificacao.localInstalacao} onChange={(v) => set("identificacao", "localInstalacao", v)} placeholder={sugestao("localInstalacao")} />
                <Campo label="Fabricante" value={identificacao.fabricante} onChange={(v) => set("identificacao", "fabricante", v)} placeholder={sugestao("fabricante")} />
                <Campo label="Número de série" value={identificacao.numeroSerie} onChange={(v) => set("identificacao", "numeroSerie", v)} placeholder={sugestao("numeroSerie")} />
                <Campo label="Potência (KVA)" value={identificacao.potenciaKVA} onChange={(v) => set("identificacao", "potenciaKVA", v)} placeholder={sugestao("potenciaKVA")} />
                <Campo label="Data / ano de fabricação" value={identificacao.dataFabricacao} onChange={(v) => set("identificacao", "dataFabricacao", v)} placeholder={sugestao("dataFabricacao") ?? "DD/MM/AAAA"} />
                <Campo label="Impedância (%)" value={identificacao.impedanciaPercent} onChange={(v) => set("identificacao", "impedanciaPercent", v)} />
                <Campo label="Volume de óleo (L)" value={identificacao.volumeOleoL} onChange={(v) => set("identificacao", "volumeOleoL", v)} placeholder={sugestao("volumeOleoL")} />
                <Campo label="Tensão inferior (V)" value={identificacao.tensaoInferiorV} onChange={(v) => set("identificacao", "tensaoInferiorV", v)} placeholder={sugestao("tensaoInferiorV")} />
                <Campo label="Tensão superior (KV)" value={identificacao.tensaoSuperiorKV} onChange={(v) => set("identificacao", "tensaoSuperiorKV", v)} placeholder={sugestao("tensaoSuperiorKV")} />
                <Campo label="Data de coleta" value={identificacao.dataColeta} onChange={(v) => set("identificacao", "dataColeta", v)} placeholder="DD/MM/AAAA" />
                <Campo label="Outros" value={identificacao.outros} onChange={(v) => set("identificacao", "outros", v)} />
              </div>

              {/*
                O QUE OS MODELOS DE GASES E PCB PEDEM A MAIS. Campo em branco
                sai no relatório com o valor automático que aparece dentro dele
                (cadastro do equipamento, Protocolo ou outra amostra da mesma
                OS); sem valor automático, sai em branco — nunca com o exemplo
                do modelo.
              */}
              <div className="rf-secao cfg-relatorio">
                <header className="rf-secao-head">
                  <h4>Dados do cliente</h4>
                  <span className="rf-secao-hint">relatórios FQ, Gases e PCB · em branco = valor em cinza</span>
                </header>
                <div className="form-grid">
                  <Campo label="Cliente (da OS)" value={String(analise.cliente ?? "")} onChange={() => undefined} somenteLeitura />
                  <Campo label="CNPJ" value={identificacao.cnpj} onChange={(v) => set("identificacao", "cnpj", v)} placeholder={sugestao("cnpj")} />
                  <Campo label="Cidade/UF" value={identificacao.cidadeUf} onChange={(v) => set("identificacao", "cidadeUf", v)} placeholder={sugestao("cidadeUf") ?? "Ex.: Campinas/SP"} />
                  <Campo label="Contato" value={identificacao.contato} onChange={(v) => set("identificacao", "contato", v)} placeholder={sugestao("contato")} />
                </div>
              </div>

              <div className="rf-secao cfg-relatorio">
                <header className="rf-secao-head">
                  <h4>Equipamento e coleta</h4>
                  <span className="rf-secao-hint">relatórios FQ, Gases e PCB</span>
                </header>
                <div className="form-grid">
                  <Campo label="Tipo de equipamento" value={identificacao.tipoEquipamento} onChange={(v) => set("identificacao", "tipoEquipamento", v)} placeholder={sugestao("tipoEquipamento")} />
                  <Campo label="Identificação / TAG" value={identificacao.tag} onChange={(v) => set("identificacao", "tag", v)} placeholder={sugestao("tag")} />
                  <Campo label="Tipo de óleo" value={identificacao.tipoOleo} onChange={(v) => set("identificacao", "tipoOleo", v)} placeholder={sugestao("tipoOleo")} />
                  <Campo label="Sequência de coleta" value={identificacao.sequenciaColeta} onChange={(v) => set("identificacao", "sequenciaColeta", v)} />
                  <Campo label="Ponto de amostragem" value={identificacao.pontoAmostragem} onChange={(v) => set("identificacao", "pontoAmostragem", v)} placeholder="Ex.: Válvula inferior" />
                  <Campo label="Amostrador" value={identificacao.amostrador} onChange={(v) => set("identificacao", "amostrador", v)} />
                  <CampoOpcoes label="Em operação" value={identificacao.emOperacao} onChange={(v) => set("identificacao", "emOperacao", v)} opcoes={["Sim", "Não"]} />
                  <Campo label="Motivo da coleta" value={identificacao.motivo} onChange={(v) => set("identificacao", "motivo", v)} placeholder="Ex.: Rotina" />
                  <Campo label="Temp. da amostra (°C)" value={identificacao.temperaturaAmostra} onChange={(v) => set("identificacao", "temperaturaAmostra", v)} placeholder={sugestao("temperaturaAmostra")} />
                  <Campo label="Temp. ambiente (°C)" value={identificacao.temperaturaAmbiente} onChange={(v) => set("identificacao", "temperaturaAmbiente", v)} />
                  <Campo label="Umidade relativa (%)" value={identificacao.umidadeRelativa} onChange={(v) => set("identificacao", "umidadeRelativa", v)} />
                </div>
                <p className="cfg-relatorio-auto">
                  Preenchidos sozinhos: <strong>data de entrada</strong>{" "}
                  {relDados?.automaticos.dataEntrada ? `(${relDados.automaticos.dataEntrada}, do Protocolo)` : "(do Protocolo)"}
                  {" · "}
                  <strong>data da análise</strong> (conclusão do ensaio na bancada; PCB: "Data do ensaio") ·{" "}
                  <strong>Nº do relatório</strong> (sequência própria de FQ, de Gases e de PCB, dada quando o
                  parecer do ensaio é finalizado) · <strong>histórico</strong> e <strong>evolução dos gases</strong>{" "}
                  (análises anteriores do mesmo equipamento) · <strong>limites do FQ</strong> (cadastro de limites da
                  NBR 10576, pela condição do óleo e pelo equipamento vinculado).
                </p>
              </div>
            </>
          )}

          {aba === "fisicoQuimica" && (
            <>
              <p className="modal-tab-hint">Ensaio Físico-Químico — os valores medidos em cada parâmetro.</p>
              <div className="form-grid">
                <Campo label="Aparência" value={fisicoQuimica.aparencia} onChange={(v) => set("fisicoQuimica", "aparencia", v)} />
                <Campo label="Cor" value={fisicoQuimica.cor} onChange={(v) => set("fisicoQuimica", "cor", v)} />
                <Campo label="Densidade a 20/4ºC (g/cm³)" value={fisicoQuimica.densidade20_4} onChange={(v) => set("fisicoQuimica", "densidade20_4", v)} />
                <Campo label="Tensão interfacial a 25ºC (mN/m)" value={fisicoQuimica.tensaoInterfacial25} onChange={(v) => set("fisicoQuimica", "tensaoInterfacial25", v)} />
                <Campo label="Teor de água medido (ppm)" value={fisicoQuimica.teorAgua} onChange={(v) => set("fisicoQuimica", "teorAgua", v)} />
                <Campo label="Índice de neutralização (mgKOH/g)" value={fisicoQuimica.indiceNeutralizacao} onChange={(v) => set("fisicoQuimica", "indiceNeutralizacao", v)} />
                <Campo label="Rigidez dielétrica — disco (KV)" value={fisicoQuimica.rigidezDieletrica} onChange={(v) => set("fisicoQuimica", "rigidezDieletrica", v)} />
                <Campo label="Fator de potência a 90ºC (%)" value={fisicoQuimica.fatorPotencia90} onChange={(v) => set("fisicoQuimica", "fatorPotencia90", v)} />
                <Campo label="Fator de potência a 25ºC (%)" value={fisicoQuimica.fatorPotencia25} onChange={(v) => set("fisicoQuimica", "fatorPotencia25", v)} />
                <Campo label="Fator de potência a 100ºC (%)" value={fisicoQuimica.fatorPotencia100} onChange={(v) => set("fisicoQuimica", "fatorPotencia100", v)} />
                <StatusDaSecao label="Status da seção" value={fisicoQuimica.status} onChange={(v) => set("fisicoQuimica", "status", v)} somenteLeitura={parecerTravado} motivo={motivoParecerTravado} />
                <TextareaCampo
                  label="Diagnóstico / conclusão"
                  value={fisicoQuimica.diagnostico}
                  placeholder="Os resultados indicam que o óleo mineral isolante ATENDE AOS VALORES LIMITES com base na avaliação de suas propriedades."
                  onChange={(v) => set("fisicoQuimica", "diagnostico", v)}
                  melhoravel
                  somenteLeitura={parecerTravado}
                  motivo={motivoParecerTravado}
                />
              </div>
            </>
          )}

          {aba === "agdGases" && (
            <>
              <p className="modal-tab-hint">Gases Dissolvidos (AGD) — teores em ppm.</p>
              <div className="form-grid">
                <Campo label="H2 — Hidrogênio" value={agdGases.h2} onChange={(v) => set("agdGases", "h2", v)} />
                <Campo label="O2 — Oxigênio" value={agdGases.o2} onChange={(v) => set("agdGases", "o2", v)} />
                <Campo label="N2 — Nitrogênio" value={agdGases.n2} onChange={(v) => set("agdGases", "n2", v)} />
                <Campo label="CO — Monóxido de Carbono" value={agdGases.co} onChange={(v) => set("agdGases", "co", v)} />
                <Campo label="CH4 — Metano" value={agdGases.ch4} onChange={(v) => set("agdGases", "ch4", v)} />
                <Campo label="CO2 — Dióxido de Carbono" value={agdGases.co2} onChange={(v) => set("agdGases", "co2", v)} />
                <Campo label="C2H4 — Etileno" value={agdGases.c2h4} onChange={(v) => set("agdGases", "c2h4", v)} />
                <Campo label="C2H6 — Etano" value={agdGases.c2h6} onChange={(v) => set("agdGases", "c2h6", v)} />
                <Campo label="C2H2 — Acetileno" value={agdGases.c2h2} onChange={(v) => set("agdGases", "c2h2", v)} />
                {/* Os dois totais são a SOMA dos gases (src/totais-gases.ts): só leitura,
                    e o servidor recalcula ao salvar. "Próxima amostragem" saiu — o
                    prazo vem do diagnóstico (NBR 7274). */}
                <Campo label="Total de Gases · soma automática" value={calcularTotaisGases(agdGases).total} onChange={() => {}} somenteLeitura />
                <Campo label="Total de Gases Combustíveis · soma automática" value={calcularTotaisGases(agdGases).totalCombustiveis} onChange={() => {}} somenteLeitura />
                <StatusDaSecao label="Status da seção" value={agdGases.status} onChange={(v) => set("agdGases", "status", v)} somenteLeitura={parecerTravado} motivo={motivoParecerTravado} />
                <TextareaCampo
                  label="Diagnóstico / conclusão"
                  value={agdGases.diagnostico}
                  placeholder="Os teores de gases detectados na amostra encontram-se em alerta."
                  onChange={(v) => set("agdGases", "diagnostico", v)}
                  melhoravel
                  somenteLeitura={parecerTravado}
                  motivo={motivoParecerTravado}
                />
              </div>
            </>
          )}

          {aba === "pcb" && (
            <>
              <p className="modal-tab-hint">PCB — o texto de metodologia/normas do laudo é fixo e não é editado aqui.</p>
              <div className="form-grid">
                <Campo label="Data do ensaio" value={pcb.dataEnsaio} onChange={(v) => set("pcb", "dataEnsaio", v)} placeholder="DD/MM/AAAA" />
                <Campo label="Teor de PCB e Clorado (Mg/kg)" value={pcb.teorPcbClorado} onChange={(v) => set("pcb", "teorPcbClorado", v)} />
                <StatusDaSecao label="Status da seção" value={pcb.status} onChange={(v) => set("pcb", "status", v)} somenteLeitura={parecerTravado} motivo={motivoParecerTravado} />
              </div>
            </>
          )}

          {aba === "furfuraldeido" && (
            <>
              <p className="modal-tab-hint">Furanos — teor de furfuraldeído (ppm m/m); metodologia fixa, não editada aqui.</p>
              <div className="form-grid">
                <Campo label="5-Hidroximetil-2-Furfural (5HMF)" value={furfuraldeido.hmf5} onChange={(v) => set("furfuraldeido", "hmf5", v)} />
                <Campo label="2-Furfurilalcool (2FOL)" value={furfuraldeido.fol2} onChange={(v) => set("furfuraldeido", "fol2", v)} />
                <Campo label="2-Furfural (2FAL)" value={furfuraldeido.fal2} onChange={(v) => set("furfuraldeido", "fal2", v)} />
                <Campo label="2-Acetil Furano (2ACF)" value={furfuraldeido.acf2} onChange={(v) => set("furfuraldeido", "acf2", v)} />
                <Campo label="5-Metil-2-Furfural (5MEF)" value={furfuraldeido.mef5} onChange={(v) => set("furfuraldeido", "mef5", v)} />
                <Campo label="Grau de polimerização (GP)" value={furfuraldeido.grauPolimerizacao} onChange={(v) => set("furfuraldeido", "grauPolimerizacao", v)} />
                <StatusDaSecao label="Status da seção" value={furfuraldeido.status} onChange={(v) => set("furfuraldeido", "status", v)} somenteLeitura={parecerTravado} motivo={motivoParecerTravado} />
              </div>
            </>
          )}

          {/* O diagnóstico só abre com a OS inteira concluída — enquanto não
              estiver, a aba diz o que falta em vez de oferecer um botão que o
              servidor recusaria. */}
          {aba === "diagnostico" && diagnosticoBloqueado && (
            <div className="diag">
              <div className="modal-notice">
                <span className="modal-notice-ic">🔒</span>
                <span>{diagnosticoBloqueado}</span>
              </div>
            </div>
          )}

          {aba === "diagnostico" && !diagnosticoBloqueado && (
            <div className="diag">
              <p className="modal-tab-hint">
                O diagnóstico compara cada resultado com a tabela de <strong>Limites normativos</strong>.
                Parâmetro sem limite cadastrado <strong>não recebe veredito</strong> — o sistema diz o que
                falta em vez de adivinhar.
              </p>

              {/*
                Gerar o diagnóstico é ato de quem tem a permissão (o servidor
                recusa com 403 — ver server/analise-diagnostico.ts). Sem o
                aviso, o botão ficava aceso para todo mundo e só dizia "não" no
                clique, sem explicar de quem é a etapa.
              */}
              {!podeDiagnosticar && (
                <div className="modal-notice">
                  <span className="modal-notice-ic">🔒</span>
                  <span>
                    O diagnóstico é restrito a quem tem a permissão. Peça a um administrador do
                    laboratório na aba <strong>“Acesso e permissões”</strong> — os resultados
                    continuam podendo ser lançados normalmente.
                  </span>
                </div>
              )}

              <div className="diag-acoes">
                <button
                  className="btn-secondary"
                  onClick={() => rodarDiagnostico(false)}
                  disabled={diagnosticando || jaDespachada || !podeDiagnosticar}
                  title={podeDiagnosticar ? undefined : "Etapa restrita — peça a permissão"}
                >
                  <IconFlask size={15} /> {diagnosticando ? "Analisando..." : "Gerar diagnóstico"}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => rodarDiagnostico(true)}
                  disabled={diagnosticando || jaDespachada || !podeDiagnosticar}
                  title={
                    podeDiagnosticar
                      ? "A IA só melhora a redação — os números e vereditos vêm do motor"
                      : "Etapa restrita — peça a permissão"
                  }
                >
                  Gerar e melhorar o texto com IA
                </button>
              </div>

              {diagInfo?.aviso && <div className="diag-aviso">{diagInfo.aviso}</div>}
              {diagInfo?.mensagem && (
                <div className={`diag-msg${diag?.incompleto ? " parcial" : ""}`}>{diagInfo.mensagem}</div>
              )}

              {diag?.semLimite?.length > 0 && (
                <div className="diag-faltando">
                  <strong>Sem limite cadastrado ({diag.semLimite.length}):</strong>{" "}
                  {diag.semLimite.join(" · ")}
                </div>
              )}

              {/* Tipo de falha pelas razões entre gases (NBR 7274): pergunta
                  separada da severidade — uma diz "o quanto", a outra "o quê". */}
              {razoes && (
                <div className="diag-secao diag-razoes">
                  <div className="diag-secao-head">
                    <strong>Tipo de falha · NBR 7274</strong>
                    {razoes.caso ? (
                      <span className="pill pill-amber">
                        {razoes.caso.caso} — {razoes.caso.rotulo}
                      </span>
                    ) : (
                      <span className="pill pill-gray">sem caso definido</span>
                    )}
                  </div>
                  <p className="diag-resumo">{razoes.mensagem}</p>
                  <table className="data-table diag-tabela">
                    <thead>
                      <tr>
                        <th>Razão</th>
                        <th>Conta</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {razoes.razoes?.map((r: any) => (
                        <tr key={r.chave}>
                          <td>{r.formula}</td>
                          <td>
                            {r.numerador?.valor ?? "—"} / {r.denominador?.valor ?? "—"}
                          </td>
                          <td>
                            {r.valor !== null && r.valor !== undefined ? (
                              <strong>{r.valor}</strong>
                            ) : (
                              <span className="muted">não calculada — {r.motivo}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {razoes.caso?.norma && (
                    <p className="diag-fonte">
                      Fonte da tabela: {razoes.caso.norma}
                      {razoes.caso.revisao ? ` ${razoes.caso.revisao}` : ""} — cadastrada na aba
                      “Razões NBR 7274”.
                    </p>
                  )}
                </div>
              )}

              {diag?.secoes?.map((sec: any) => (
                <div className="diag-secao" key={sec.secao}>
                  <div className="diag-secao-head">
                    <strong>{SECOES.find((x) => x.key === sec.secao)?.label ?? sec.secao}</strong>
                    <span className={`pill pill-${vereditoTone(sec.veredito)}`}>
                      {sec.veredito ?? "Indeterminado"}
                    </span>
                  </div>
                  <p className="diag-resumo">{sec.resumo}</p>
                  <table className="data-table diag-tabela">
                    <thead>
                      <tr>
                        <th>Parâmetro</th>
                        <th>Medido</th>
                        <th>Faixa</th>
                        <th>Veredito</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sec.parametros
                        .filter((pa: any) => pa.situacao !== "sem-valor")
                        .map((pa: any) => (
                          <tr key={pa.parametro}>
                            <td>{pa.rotulo}</td>
                            <td>
                              {pa.valorInformado ?? "—"}
                              {pa.unidade ? ` ${pa.unidade}` : ""}
                            </td>
                            <td className="diag-faixa">{pa.faixa ? `${pa.faixa} (${pa.norma})` : "—"}</td>
                            <td>
                              {pa.veredito ? (
                                <span className={`pill pill-${vereditoTone(pa.veredito)}`}>
                                  {pa.veredito}
                                </span>
                              ) : (
                                <span className="diag-sem" title={pa.explicacao}>
                                  {pa.situacao === "sem-limite" ? "sem limite" : "não avaliado"}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ))}

              {/* A sugestão de resultado NÃO é aplicada sozinha: quem assina é gente. */}
              {diag?.resultadoSugerido && (
                <div className="diag-sugestao">
                  Resultado sugerido: <strong>{diag.resultadoSugerido}</strong> — confirme na aba
                  Despacho. A sugestão não é aplicada sozinha.
                </div>
              )}

              <div className="diag-aprovacao">
                <strong>Aprovação do laudo</strong>
                {aprovacao?.aprovacao ? (
                  <p className={`diag-status ${aprovacao.podeDespachar ? "ok" : "pendente"}`}>
                    {aprovacao.aprovacao.status}
                    {aprovacao.aprovacao.avaliado_por ? ` por ${aprovacao.aprovacao.avaliado_por}` : ""}
                    {aprovacao.motivo ? ` — ${aprovacao.motivo}` : ""}
                  </p>
                ) : (
                  <p className="muted">Ainda não enviado para aprovação.</p>
                )}
                <button
                  className="btn-secondary"
                  onClick={enviarParaAprovacao}
                  disabled={diagnosticando || jaDespachada}
                >
                  <IconSend size={15} /> Enviar para aprovação
                </button>
                <span className="muted">
                  O laudo só pode ser despachado depois que outra pessoa aprovar.
                </span>
              </div>
            </div>
          )}

          {aba === "despacho" && (
            <>
              <p className="modal-tab-hint">
                Ao despachar, o laudo é gerado automaticamente com o template preenchido (apenas as
                seções selecionadas na solicitação) e enviado ao Relatórios.
              </p>
              <div className="form-grid">
                <Campo
                  label="Responsável"
                  value={responsavel}
                  onChange={setResponsavel}
                  placeholder="Técnico do laboratório"
                />
                <TextareaCampo
                  label="Parecer técnico (diagnóstico)"
                  value={parecer}
                  onChange={setParecer}
                  melhoravel
                />
              </div>
              {laudoNome ? (
                <div className="form-ok" style={{ marginTop: 14 }}>
                  <IconCheck size={14} /> Laudo gerado: {laudoNome} (será regerado com os dados
                  atuais ao despachar)
                </div>
              ) : (
                <p className="muted" style={{ marginTop: 14 }}>
                  O laudo será gerado automaticamente ao despachar.
                </p>
              )}

              {/*
                UM POR AMOSTRA E UM POR ENSAIO. O da amostra junta os relatórios
                de todos os ensaios (é o que vai ao Relatórios); o de cada ensaio
                é o mesmo documento, sozinho. O Nº de Gases e de PCB só existe
                depois de o parecer daquele ensaio ser finalizado.
              */}
              <div className="rf-secao cfg-relatorio">
                <header className="rf-secao-head">
                  <h4>Relatórios</h4>
                  <span className="rf-secao-hint">um arquivo por amostra ou um por ensaio</span>
                </header>
                <div className="cfg-relatorios-lista">
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => void baixarRelatorio(null)}
                    disabled={ocupado}
                    title="Todos os ensaios desta amostra num arquivo só — o mesmo que vai ao Relatórios"
                  >
                    <IconDownload size={15} />
                    {baixando === "amostra" ? "Gerando..." : "Amostra completa"}
                  </button>
                  {(relDados?.ensaios ?? []).map((e) => (
                    <button
                      key={e.ensaio}
                      type="button"
                      className="btn-outline"
                      onClick={() => void baixarRelatorio(e.ensaio)}
                      disabled={ocupado}
                      title={
                        e.modelo === "antigo"
                          ? "Modelo antigo do laudo (até chegar o modelo próprio deste ensaio)"
                          : e.numero
                            ? `Relatório Nº ${e.numero}`
                            : e.parecerFinalizado
                              ? `Recebe o Nº ${e.proximo} ao ser gerado`
                              : "Parecer ainda não finalizado: sai sem número (rascunho)"
                      }
                    >
                      <IconDownload size={15} />
                      {baixando === e.ensaio ? "Gerando..." : ROTULO_SECAO[e.ensaio] ?? e.ensaio}
                      {e.modelo === "novo" && (
                        <span className="cfg-relatorio-num">
                          {e.numero ? `Nº ${e.numero}` : e.parecerFinalizado ? "novo Nº" : "rascunho"}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {jaDespachada && (
            <div className="form-error" style={{ marginTop: 14 }}>
              Esta análise já foi despachada ao Relatórios. O laudo entregue não muda mais:
              resultados, parecer e novo despacho estão bloqueados. Para corrigir algo, abra uma
              nova análise para a mesma OS.
            </div>
          )}
          {erro && (
            <div className="form-error" style={{ marginTop: 14 }}>
              {erro}
            </div>
          )}
          {ok && (
            <div className="form-ok" style={{ marginTop: 14 }}>
              {ok}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Fechar
          </button>
          <div className="modal-foot-actions">
            <button type="button" className="btn-secondary" onClick={salvar} disabled={ocupado}>
              {salvando ? "Salvando..." : "Salvar"}
            </button>
            <button type="button" className="btn-primary" onClick={despachar} disabled={ocupado}>
              {despachando ? "Enviando..." : "Despachar ao Relatórios"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Campo({
  label,
  value,
  onChange,
  placeholder,
  somenteLeitura,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Campo calculado (ex.: totais de gases): mostra, não deixa digitar. */
  somenteLeitura?: boolean;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="text"
        value={value ?? ""}
        placeholder={placeholder}
        readOnly={somenteLeitura}
        tabIndex={somenteLeitura ? -1 : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** Campo de escolha curta (ex.: Em operação: Sim / Não). Vazio = não informado. */
function CampoOpcoes({
  label,
  value,
  onChange,
  opcoes,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  opcoes: string[];
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">— não informado —</option>
        {opcoes.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatusDaSecao({
  label,
  value,
  onChange,
  required,
  somenteLeitura,
  motivo,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  required?: boolean;
  /** Travado: o veredito é da etapa de diagnóstico, não do lançamento. */
  somenteLeitura?: boolean;
  motivo?: string;
}) {
  if (somenteLeitura) {
    return (
      <div className="field field-wide" title={motivo}>
        <span>
          {label} <em className="campo-travado-ic">🔒</em>
        </span>
        <div className="campo-travado">
          {value?.trim() ? value : <span className="muted">ainda sem veredito</span>}
          {motivo && <em className="campo-travado-motivo">{motivo}</em>}
        </div>
      </div>
    );
  }
  return (
    <div className="field field-wide">
      <span>
        {label}
        {required && <em className="req"> *</em>}
      </span>
      <StatusCardSelector
        options={STATUS_SECAO_OPCOES}
        hints={STATUS_SECAO_HINTS}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

function TextareaCampo({
  label,
  value,
  onChange,
  placeholder,
  melhoravel,
  somenteLeitura,
  motivo,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Mostra o botão que pede à IA para reescrever o texto (nunca inventa valores). */
  melhoravel?: boolean;
  /** Travado: o texto é da etapa de diagnóstico, não do lançamento. */
  somenteLeitura?: boolean;
  motivo?: string;
}) {
  const [melhorando, setMelhorando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  async function melhorar() {
    const texto = (value ?? "").trim();
    if (!texto) {
      setAviso("Escreva o texto antes de melhorar.");
      return;
    }
    setMelhorando(true);
    setAviso(null);
    try {
      const r = await api.gpt.melhorarParecer(texto);
      onChange(r.improved);
    } catch (err) {
      setAviso(err instanceof ApiError ? err.message : "Não foi possível melhorar agora.");
    } finally {
      setMelhorando(false);
    }
  }

  if (somenteLeitura) {
    return (
      <div className="field field-wide" title={motivo}>
        <span>
          {label} <em className="campo-travado-ic">🔒</em>
        </span>
        <div className="campo-travado campo-travado-texto">
          {value?.trim() ? value : <span className="muted">ainda sem parecer</span>}
          {motivo && <em className="campo-travado-motivo">{motivo}</em>}
        </div>
      </div>
    );
  }

  return (
    <label className="field field-wide">
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span>{label}</span>
        {melhoravel && (
          <button
            type="button"
            onClick={melhorar}
            disabled={melhorando}
            title="Reescreve em linguagem técnica formal — não inventa valores nem resultados"
            style={{
              fontSize: 12,
              padding: "2px 10px",
              borderRadius: 999,
              border: "1px solid #38bdf8",
              background: "rgba(56,189,248,0.12)",
              color: "#0ea5e9",
              cursor: melhorando ? "default" : "pointer",
              opacity: melhorando ? 0.6 : 1,
              whiteSpace: "nowrap",
            }}
          >
            {melhorando ? "Melhorando..." : "✨ Melhorar com IA"}
          </button>
        )}
      </span>
      <textarea rows={3} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      {aviso && (
        <span className="muted" style={{ marginTop: 4, fontSize: 12 }}>
          {aviso}
        </span>
      )}
    </label>
  );
}
